// Share helpers. Uses the Web Share API when available (mobile + many
// desktop browsers), falls back to copying the URL to the clipboard, and
// finally to a synthetic <a> click for the most pessimistic environment.

export interface ShareInput {
  title?: string;
  text?: string;
  url: string;
}

export type ShareOutcome = "shared" | "copied" | "failed";

export async function share(input: ShareInput): Promise<ShareOutcome> {
  // Native share sheet
  const nav = navigator as Navigator & { share?: (d: ShareInput) => Promise<void> };
  if (typeof nav.share === "function") {
    try {
      await nav.share(input);
      return "shared";
    } catch (e) {
      // User cancelled — silently treat as no-op. Other errors fall through.
      if ((e as DOMException)?.name === "AbortError") return "shared";
    }
  }
  // Clipboard fallback
  try {
    await navigator.clipboard.writeText(input.url);
    return "copied";
  } catch {
    /* keep falling through */
  }
  // Last resort: open the URL in a new tab so the user can copy it themselves.
  try {
    window.open(input.url, "_blank", "noopener");
    return "shared";
  } catch {
    return "failed";
  }
}

/**
 * Build a "directions" deep link. Prefers the user's installed maps app via
 * the platform-neutral `geo:` URI (Android + some iOS apps), but most
 * browsers will just fall back to opening OSM directions in a new tab.
 */
export function directionsUrl(lat: number, lng: number, label?: string): string {
  const params = new URLSearchParams();
  params.set("mlat", String(lat));
  params.set("mlon", String(lng));
  if (label) params.set("query", label);
  // Open OSM with a marker + opportunity to start directions.
  return `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=17/${lat}/${lng}`;
}
